// src/services/api/agent.api.ts
import { apiClient } from '../client'
import type {
  AgentEnvironment,
  CreateEnvironmentPayload,
  MountResponse,
  StartAnalysisResponse,
  ContinueConversationResponse,
  VulnerabilityTypesResponse,
} from '../../app/pages/agent/types'

/**
 * Agent Environments API — vulnerability-analysis sandbox environments.
 * Paths preserved from the original standalone client; auth/interceptors come
 * from the shared apiClient (cookie + CSRF based).
 */
export const agentApi = {
  createEnvironment: async (config: CreateEnvironmentPayload): Promise<AgentEnvironment> => {
    const { data } = await apiClient.post('/patcher/environments/', config)
    return data
  },

  getEnvironment: async (environmentId: string): Promise<AgentEnvironment> => {
    const { data } = await apiClient.get(`/patcher/environments/${environmentId}`)
    return data
  },

  listUserEnvironments: async (): Promise<AgentEnvironment[]> => {
    const { data } = await apiClient.get('/patcher/environments/user/')
    return data
  },

  startContainer: async (environmentId: string) => {
    const { data } = await apiClient.post(`/patcher/environments/${environmentId}/start-container/`)
    return data
  },

  stopContainer: async (environmentId: string) => {
    const { data } = await apiClient.post(`/patcher/environments/${environmentId}/stop/`)
    return data
  },

  deleteEnvironment: async (environmentId: string, force = false) => {
    const { data } = await apiClient.delete(`/patcher/environments/${environmentId}?force=${force}`)
    return data
  },

  getSSHKey: async (environmentId: string) => {
    const { data } = await apiClient.get(`/patcher/environments/${environmentId}/ssh-key/`)
    return data
  },

  mountDirectories: async (environmentId: string): Promise<MountResponse> => {
    const { data } = await apiClient.post(`/patcher/environments/${environmentId}/mount/`)
    return data
  },

  startAnalysis: async (
    environmentId: string,
    vulnerabilityType: string,
  ): Promise<StartAnalysisResponse> => {
    const { data } = await apiClient.post(`/patcher/environments/${environmentId}/analyze/`, {
      vulnerability_type: vulnerabilityType,
    })
    return data
  },

  continueConversation: async (
    environmentId: string,
    message: string,
    sessionId: string | null = null,
  ): Promise<ContinueConversationResponse> => {
    const payload: { message: string; session_id?: string } = { message }
    if (sessionId) payload.session_id = sessionId
    const { data } = await apiClient.post(`/patcher/environments/${environmentId}/continue/`, payload)
    return data
  },

  getVulnerabilityTypes: async (): Promise<VulnerabilityTypesResponse> => {
    const { data } = await apiClient.get('/patcher/vulnerability-types/')
    return data
  },

  checkEnvironmentStatus: async (environmentId: string) => {
    const { data } = await apiClient.get(`/patcher/environments/${environmentId}/status/`)
    return data
  },
}
